"use client";

import { CTA_SOURCES, PAGE_SECTIONS } from "@punchup/analytics";
import { ComedianLandingPageEvents, UserEvents } from "@/analytics/mixpanel";
import { NamedError, SentryFeatureTags, logException } from "@punchup/sentry";
import {
  ShowInfo,
  ShowWithComedian,
  getShows,
  processShowData,
} from "@/data/shows";
import {
  ShowLessButton,
  ViewMoreButton,
} from "@punchup/ui-common/components/common";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useExperiment } from "statsig-react";
import { usePathname, useSearchParams } from "next/navigation";

import { AlertCircle } from "lucide-react";
import { Comedian } from "@/data/comedians";
import { Database } from "@punchup/data/supabase.types";
import { isShowVisibleOnComedianPageListing } from "@punchup/data/models";
import { ResponsiveShowRow } from "@/components/shows";
import SubscribeForShows from "./SubscribeForShows";
import { WaitlistSubscription } from "@punchup/data/client";
import { cn } from "@punchup/utils/cn";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import dayjs from "@punchup/utils/dayjs";
import { filterOutPasswordProtectedShows } from "@punchup/tixologi";
import { trackEvent } from "@punchup/analytics";
import { updateTixologiAvailabilityClient } from "@/utils/tixologi-availability";
import { useIsAuthorizedToSeeDashboards } from "@/hooks";
import { useMultipleWaitlistStatus } from "@punchup/venues/client";
import { useUserComedianFollows } from "@/hooks/follows/useUserComedianFollows";
import { useNearbyShows } from "@/hooks/shows";
import { useUserLocation } from "@/hooks/user";
import { getApiClient } from "@punchup/utils/apiRegistry";

const RADIUS = 100; // 100 miles
const DEFAULT_NUMBER_OF_SHOWS_TO_DISPLAY = 5;

export function deduplicateByShowId(
  shows: ShowWithComedian[]
): ShowWithComedian[] {
  const seenIds = new Set<string>();
  return shows.filter((show) => {
    if (seenIds.has(show.id)) return false;
    seenIds.add(show.id);
    return true;
  });
}

export function filterOverrideShows(
  shows: ShowWithComedian[],
  overrideComedianIds: string[]
): { visible: ShowWithComedian[]; comedianMap: Map<string, Comedian> } {
  const visible = shows.filter((show) =>
    overrideComedianIds.some((id) =>
      isShowVisibleOnComedianPageListing(show, id)
    )
  );
  const comedianMap = new Map<string, Comedian>();
  visible.forEach((show) => {
    const match = show.show_comedians?.find(
      (pair) =>
        overrideComedianIds.includes(pair.id) &&
        pair.hidden_from_comedian_page === false
    );
    if (match) comedianMap.set(show.id, match);
  });
  return { visible, comedianMap };
}

export default function UpcomingShows({
  comedian,
  onDoneLoading,
  title,
  showSubscribe = true,
  shouldExpandShows = false,
  overrideComedianIds,
  pageSection,
  ctaSource,
  allShowsLabel = "All shows",
  allShowsHeaderClassName = "text-primary-blue font-semibold text-lg md:text-xl mb-2 md:mb-4",
}: {
  comedian: Comedian;
  onDoneLoading?: () => void;
  title?: string;
  showSubscribe?: boolean;
  shouldExpandShows?: boolean;
  overrideComedianIds?: string[];
  pageSection?: PAGE_SECTIONS;
  ctaSource?: CTA_SOURCES;
  // Override the "All shows" list header (label + classes). Defaults preserve the
  // existing comedian Tours-tab appearance; callers can relabel/restyle just this
  // header for their surface.
  allShowsLabel?: string;
  allShowsHeaderClassName?: string;
}) {
  const [allShows, setAllShows] = useState<ShowInfo[] | null>(null);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [showsError, setShowsError] = useState<string | null>(null);
  const [nearbyShows, setNearbyShows] = useState<ShowInfo[]>([]);
  const [isLoadingLocalShows, setIsLoadingLocalShows] = useState<boolean>(true);
  // Maps show ID → the comedian to display in the row when overrideComedianIds is set,
  // since the page comedian is not the same as the show's comedian.
  const [overrideShowComedianMap, setOverrideShowComedianMap] = useState<
    Map<string, Comedian>
  >(new Map());
  const supabase = createClientComponentClient<Database>();
  const { zipcode, countryCode } = useNearbyShows({ comedianId: comedian.id });
  const [buyTicketsShowId, setBuyTicketsShowId] = useState<string>("");
  const pathname = usePathname();
  const { location: userLocation } = useUserLocation();

  const { comediansAuthorized } = useIsAuthorizedToSeeDashboards();
  const { isFollowing } = useUserComedianFollows();

  // A/B test for ticket page scroll behavior
  const ticketScrollExperiment = useExperiment(
    "comedian-tickets-page-scroll-behavior"
  );
  const showScrollPositionExperiment = ticketScrollExperiment.config.get(
    "scroll_to_shows_directly",
    false
  );

  // This is used to distinguish whether this component is being rendered on /<comedian>/tickets or /<comedian>/tour and not on /<comedian> nor /<comedian>/<special>
  const isTicketsPath =
    pathname.includes("/tickets") || pathname.includes("/tour");

  // Used to hide local shows using the query param ?localShows=false
  const searchParams = useSearchParams();
  const hideLocalShows =
    searchParams.get("localShows") === "false" ? true : false;

  // Used to highlight a specific show in the list
  const highlightShowId = searchParams.get("showId");

  const shortCode = searchParams.get("sc");

  // This is used to determine where the user came from, for example QR code, or Southpaw email
  // It is NOT a referrer, because these visits will not have a referrer
  const source = searchParams.get("src");
  const southpawJobId = searchParams.get("sid");

  // Modify location to include shortlink code for mixpanel tracking
  const location = source || "comedian-page";

  const trackSourceEvent = useCallback(() => {
    // No need to run this event in the non /tickets paths that display this component
    if (comedian.id && isTicketsPath) {
      trackEvent(UserEvents.TICKETS_COMPONENT_LOADED, {
        ComedianId: comedian.id,
        ComedianName: comedian.display_name,
        Source: location,
        ...(source ? { InternalReferrer: source } : {}),
        ...(southpawJobId ? { SouthpawJobId: southpawJobId } : {}),
        ...(shortCode ? { ShortLinkCode: shortCode } : {}),
        ...(pageSection ? { page_section: pageSection } : {}),
      });
    }
  }, [
    comedian.id,
    comedian.display_name,
    isTicketsPath,
    location,
    source,
    southpawJobId,
    shortCode,
    pageSection,
  ]);

  useEffect(() => {
    trackSourceEvent();
  }, [trackSourceEvent]);

  useEffect(() => {
    if (
      allShows &&
      (allShows.length > 0 || (allShows.length === 0 && !isLoadingLocalShows))
    ) {
      onDoneLoading && onDoneLoading();
    }
  }, [allShows, isLoadingLocalShows, onDoneLoading]);

  useEffect(() => {
    const getTourDatesNearUser = async () => {
      if (!zipcode || !countryCode) {
        setIsLoadingLocalShows(false);
        return;
      }

      // Podcast override: fetch nearby shows for each member comedian instead of the page comedian.
      // Shows that feature both members will appear once after deduplication.
      // This is experimental for the Bein Ian with Jordan podcast.
      if (overrideComedianIds && overrideComedianIds.length > 0) {
        let merged: ShowWithComedian[] = [];
        try {
          const results = await Promise.all(
            overrideComedianIds.map((id) =>
              getApiClient().get<ShowWithComedian[]>(
                // Omit startDatetime so /api/shows uses its default 12h-after-start
                // grace cutoff (same as "All shows"). An exact UTC instant dropped
                // nearby shows before showtime — see TIC-535.
                `/api/shows?postalCode=${zipcode}&countryCode=${countryCode}&radius=${RADIUS}&comedianId=${id}`
              )
            )
          );
          merged = results.flat();
        } catch (err) {
          logException(
            new NamedError(
              "GET /api/shows",
              err instanceof Error ? err.message : String(err)
            ),
            {
              data: `[UpcomingShows] Error fetching nearby shows for override comedians ${overrideComedianIds.join(",")} near user with zipcode ${zipcode} and countryCode ${countryCode}`,
              tags: {
                comedianId: overrideComedianIds.join(","),
                zipcode,
                countryCode,
                api: true,
              },
            }
          );
        }

        if (merged.length) {
          const deduped = deduplicateByShowId(merged);
          const showsWithAvailability =
            await updateTixologiAvailabilityClient(deduped);
          const { visible, comedianMap } = filterOverrideShows(
            showsWithAvailability,
            overrideComedianIds
          );
          setOverrideShowComedianMap(
            (prev) => new Map([...prev, ...comedianMap])
          );
          setNearbyShows(visible.map((show) => processShowData(show)));
        }
        setIsLoadingLocalShows(false);
        return;
      } // End of podcast override

      let shows: ShowWithComedian[] = [];
      try {
        shows = await getApiClient().get<ShowWithComedian[]>(
          // Omit startDatetime so /api/shows uses its default 12h-after-start
          // grace cutoff (same as "All shows"). An exact UTC instant dropped
          // nearby shows before showtime — see TIC-535.
          `/api/shows?postalCode=${zipcode}&countryCode=${countryCode}&radius=${RADIUS}&comedianId=${comedian.id}`
        );
      } catch (err) {
        logException(
          new NamedError(
            "GET /api/shows",
            err instanceof Error ? err.message : String(err)
          ),
          {
            data: `[UpcomingShows] Error fetching shows for comedian ${comedian.id} near user with zipcode ${zipcode} and countryCode ${countryCode}`,
            tags: {
              comedianId: comedian.id,
              zipcode,
              countryCode,
              api: true,
            },
          }
        );
      }

      if (shows?.length) {
        const showsWithAvailability =
          await updateTixologiAvailabilityClient(shows);
        const showInfoArray = showsWithAvailability
          .filter((show) =>
            isShowVisibleOnComedianPageListing(show, comedian.id)
          )
          .map((show) => processShowData(show));

        setNearbyShows(showInfoArray);
      }
      setIsLoadingLocalShows(false);
    };

    getTourDatesNearUser();
  }, [comedian.id, allShows, zipcode, countryCode, overrideComedianIds]);

  useEffect(() => {
    const fetchShows = async () => {
      // Podcast override: fetch all shows for each member comedian instead of the page comedian.
      // Results are merged, deduplicated, and sorted by date before being displayed.
      // This is experimental for the Bein Ian with Jordan podcast.
      if (overrideComedianIds && overrideComedianIds.length > 0) {
        try {
          const results = await Promise.all(
            overrideComedianIds.map((id) =>
              getShows(supabase, {
                limitDays: undefined,
                comedianId: id,
                includeNonPublicShows: false,
              })
            )
          );
          const deduped = deduplicateByShowId(results.flat());
          deduped.sort((a, b) => dayjs(a.datetime).diff(dayjs(b.datetime)));

          const showsWithAvailability =
            await updateTixologiAvailabilityClient(deduped);
          const showsWithoutPasswordProtection =
            await filterOutPasswordProtectedShows(showsWithAvailability);

          const { visible, comedianMap } = filterOverrideShows(
            showsWithoutPasswordProtection,
            overrideComedianIds
          );
          setOverrideShowComedianMap(
            (prev) => new Map([...prev, ...comedianMap])
          );
          setAllShows(visible.map((show) => processShowData(show)));

          if (
            deduped.some((show) => show.id === highlightShowId) ||
            shouldExpandShows
          ) {
            setShowAll(true);
          }
        } catch (error) {
          logException(
            new NamedError(
              "fetchShows override (UpcomingShows)",
              error instanceof Error ? error.message : String(error)
            ),
            {
              extra: { overrideComedianIds },
              tags: {
                feature: SentryFeatureTags.Comedian,
                comedian: comedian.display_name,
              },
            }
          );
          setShowsError(
            "Oops. Something went wrong fetching shows. Please try again later."
          );
        }
        return;
      } // End of podcast override

      const isComedianManager = comediansAuthorized.includes(comedian.id);
      const payload = {
        limitDays: undefined,
        comedianId: comedian.id,
        includeNonPublicShows:
          !!comedian.is_external_preview || !!isComedianManager,
      };

      try {
        const shows = await getShows(supabase, payload);
        const showsWithAvailability =
          await updateTixologiAvailabilityClient(shows);
        const showsWithoutPasswordProtection =
          await filterOutPasswordProtectedShows(showsWithAvailability);

        const showInfoArray = showsWithoutPasswordProtection
          .filter((show) =>
            isShowVisibleOnComedianPageListing(show, comedian.id)
          )
          .map((show) => processShowData(show));
        setAllShows(showInfoArray);

        // if one of the Shows matches the specified Show ID to highlight
        const highlight = shows.some((show) => show.id === highlightShowId);
        if (highlight || shouldExpandShows) {
          // ensure we display all shows
          setShowAll(true);
        }
      } catch (error) {
        logException(
          new NamedError(
            "fetchShows (UpcomingShows)",
            error instanceof Error ? error.message : String(error)
          ),
          {
            extra: {
              payload,
              isComedianManager,
            },
            tags: {
              feature: SentryFeatureTags.Comedian,
              comedian: comedian.display_name,
            },
          }
        );
        setShowsError(
          "Oops. Something went wrong fetching shows. Please try again later."
        );
      }
    };
    fetchShows();
  }, [
    comedian.id,
    highlightShowId,
    shouldExpandShows,
    supabase,
    overrideComedianIds,
    comediansAuthorized,
    comedian.is_external_preview,
    comedian.display_name,
  ]);

  const handleSeeAllClick = useCallback(() => {
    trackEvent(ComedianLandingPageEvents.SEE_ALL_SHOWS_CLICKED);
    setShowAll(true);
  }, []);

  const handleSeeLessClick = useCallback(() => {
    trackEvent(ComedianLandingPageEvents.SHOW_LESS_SHOWS_CLICKED);
    setShowAll(false);
  }, []);

  const handleSetBuyTicketsShowId = useCallback(
    (id: string | ((prevId: string) => string)) => {
      setBuyTicketsShowId(id);
    },
    []
  );

  // Batch-fetch waitlist status for every loaded sold-out show in one
  // request — see [[ShowRowContextValue.waitlistSubscription]]. Without this
  // each ShowRowBuyButton would fire its own /api/v2/user/waitlist call and
  // dense comedian pages would blow the rate limit.
  const soldOutShowsForWaitlist = useMemo(() => {
    const out: Array<{ id: string; ownerId: string; isVenue: boolean }> = [];
    const seen = new Set<string>();
    for (const s of [...nearbyShows, ...(allShows ?? [])]) {
      if (!s.soldOut || seen.has(s.id)) continue;
      seen.add(s.id);
      out.push({ id: s.id, ownerId: comedian.id, isVenue: false });
    }
    return out;
  }, [allShows, nearbyShows, comedian.id]);

  const { subscriptions: waitlistSubscriptions } = useMultipleWaitlistStatus(
    soldOutShowsForWaitlist
  );

  // Stable per-show lookup so renderShowRow's memo dep set doesn't churn.
  const waitlistSubscriptionsByShowId = useMemo(() => {
    const map = new Map<string, WaitlistSubscription>();
    for (const sub of waitlistSubscriptions) {
      map.set(sub.show_id, sub);
    }
    return map;
  }, [waitlistSubscriptions]);

  const renderShowRow = useCallback(
    (show: ShowInfo, isNearby = false) => {
      const showComedian = overrideShowComedianMap.get(show.id) ?? comedian;
      return (
        <ResponsiveShowRow
          key={show.id}
          shows={[show]}
          comedian={showComedian}
          location={location}
          {...(source ? { InternalReferrer: source } : {})}
          buyTicketsShowId={buyTicketsShowId}
          setBuyTicketsShowId={handleSetBuyTicketsShowId}
          highlight={highlightShowId === show.id}
          {...(isNearby ? { isNearby: true } : {})}
          hideHeatingUp
          hideComedianAvatar={!overrideComedianIds}
          hideUsersChecking
          hideComedianName
          {...(overrideComedianIds
            ? { isFollowed: isFollowing(showComedian.id) }
            : {})}
          {...(pageSection ? { pageSection } : {})}
          {...(ctaSource ? { ctaSource } : {})}
          waitlistSubscription={
            waitlistSubscriptionsByShowId.get(show.id) ?? null
          }
        />
      );
    },
    [
      overrideShowComedianMap,
      comedian,
      location,
      source,
      buyTicketsShowId,
      handleSetBuyTicketsShowId,
      highlightShowId,
      overrideComedianIds,
      isFollowing,
      pageSection,
      ctaSource,
      waitlistSubscriptionsByShowId,
    ]
  );

  const trimNearbyShows = useCallback(
    (shows: ShowInfo[]) => {
      // If shouldExpandShows is true, always show all nearby shows
      if (shouldExpandShows) {
        return shows;
      }

      // if there are enough nearby shows to meet the default, return them
      if (shows.length >= DEFAULT_NUMBER_OF_SHOWS_TO_DISPLAY) {
        return shows.slice(undefined, DEFAULT_NUMBER_OF_SHOWS_TO_DISPLAY);
      }

      // otherwise, we must have less than the default number so return all shows
      return shows;
    },
    [shouldExpandShows]
  );

  const trimAllShows = useCallback(
    (shows: ShowInfo[]) => {
      // If shouldExpandShows is true, always show all shows
      if (shouldExpandShows) {
        return shows;
      }

      // if we render the default number of nearby shows, don't return any of "All shows"
      if (nearbyShows.length >= DEFAULT_NUMBER_OF_SHOWS_TO_DISPLAY) {
        return [];
      }

      // otherwise, return only enough "All shows" to ensure the total count is the default
      return shows.slice(
        undefined,
        DEFAULT_NUMBER_OF_SHOWS_TO_DISPLAY - nearbyShows.length
      );
    },
    [shouldExpandShows, nearbyShows.length]
  );

  if (showsError) {
    return (
      <>
        {title && (
          <div className="flex flex-row items-center justify-between mb-1 sm:mb-2">
            <h4 className="text-[28px]">{title}</h4>
          </div>
        )}
        <div className="p-4 mt-5 border border-red-200 rounded-lg bg-red-50">
          <p className="flex flex-row items-center gap-2 text-[14px] text-red-500 space-x-2">
            <AlertCircle className="w-6 h-6" />
            {showsError}
          </p>
        </div>
      </>
    );
  }

  if (allShows === null) {
    return (
      <>
        {title && (
          <div className="flex flex-row items-center justify-between mb-1 sm:mb-2">
            <h4 className="text-[28px]">{title}</h4>
          </div>
        )}
        <div className="flex flex-col gap-4">
          <div className="w-full h-20 bg-gray-200 rounded-xl animate-pulse" />
          <div className="w-full h-20 bg-gray-200 rounded-xl animate-pulse" />
          <div className="w-full h-20 bg-gray-200 rounded-xl animate-pulse" />
        </div>
      </>
    );
  }

  const allShowsLength = allShows.length;
  const nearbyShowsLength = nearbyShows.length;
  const totalShowsLength = allShowsLength + nearbyShowsLength;

  // If shouldExpandShows is true or showAll is true, show all shows
  const nearbyShowsToRender =
    showAll || shouldExpandShows ? nearbyShows : trimNearbyShows(nearbyShows);

  const allShowsToRender =
    showAll || shouldExpandShows ? allShows : trimAllShows(allShows);

  const noShows = totalShowsLength === 0 || !allShows || allShows.length === 0;

  if (noShows) {
    // always render the "SubscribeForShows" component even when the comedians has no shows
    // this allows us to always have a way for users to subscribe to a comedian's shows!
    return (
      <>
        {title && (
          <div className="flex flex-row items-center justify-between mb-1 sm:mb-2">
            <h4 className="text-[28px]">{title}</h4>
            <div />
          </div>
        )}
        <div className="flex flex-row items-center justify-center w-full px-2 py-3 mt-1 mb-2 rounded-lg bg-tertiary-blue/10">
          <p className="text-[14px] text-secondary-blue">No shows scheduled</p>
        </div>
        {!showScrollPositionExperiment && showSubscribe && (
          <SubscribeForShows
            className={cn("mb-4", title && "mt-2")}
            comedianToFollow={{
              id: comedian.id,
              slug: comedian.slug,
              display_name_short: comedian.display_name_short,
            }}
            {...(pageSection ? { pageSection } : {})}
          />
        )}
      </>
    );
  }

  return (
    <>
      {title && (
        <div className="flex flex-row items-center justify-between mb-3 md:mb-4.5">
          <h4 className="text-[28px]">{title}</h4>
          <div />
        </div>
      )}
      {!showScrollPositionExperiment && showSubscribe && (
        <SubscribeForShows
          className={cn("mb-3", title && "mt-2")}
          comedianToFollow={{
            id: comedian.id,
            slug: comedian.slug,
            display_name_short: comedian.display_name_short,
          }}
          {...(pageSection ? { pageSection } : {})}
        />
      )}
      {nearbyShowsToRender.length > 0 && (
        <div className="text-primary-blue font-semibold text-lg md:text-xl mb-2 md:mb-4">
          {`Shows near ${userLocation ? userLocation.city : "you"}`}
        </div>
      )}

      {!isLoadingLocalShows &&
        !hideLocalShows &&
        nearbyShowsToRender.length > 0 && (
          <div className="flex flex-col gap-y-4">
            {nearbyShowsToRender.map((show) => renderShowRow(show, true))}
          </div>
        )}

      {allShowsToRender.length > 0 && (
        <>
          <div
            className={cn(
              allShowsHeaderClassName,
              nearbyShowsLength > 0 && "pt-8"
            )}
          >
            {allShowsLabel}
          </div>
          <div className="flex flex-col gap-y-4">
            {allShowsToRender.map((show) => renderShowRow(show))}
          </div>
        </>
      )}

      {totalShowsLength > DEFAULT_NUMBER_OF_SHOWS_TO_DISPLAY &&
        !shouldExpandShows && (
          <div className="w-full flex flex-row justify-center items-center pt-4">
            {showAll ? (
              <ShowLessButton
                onClick={handleSeeLessClick}
                className="w-full py-5"
              />
            ) : (
              <ViewMoreButton
                label={`See All (${totalShowsLength})`}
                onClick={handleSeeAllClick}
                className="w-full py-5"
              />
            )}
          </div>
        )}
    </>
  );
}
